import { getPendingOperations, removePendingOperation, savePendingOperation, PendingOperation } from './db';
import { checkServerConnection } from './connectivity';
import { toast } from 'sonner';

let isSyncing = false;
const MAX_RETRIES = 5;

export async function processSyncQueue(): Promise<{ successCount: number; failCount: number }> {
  if (isSyncing) return { successCount: 0, failCount: 0 };
  
  const online = await checkServerConnection();
  if (!online) {
    return { successCount: 0, failCount: 0 };
  }

  isSyncing = true;
  let successCount = 0;
  let failCount = 0;

  try {
    const operations = await getPendingOperations();
    const pendingOps = operations.filter(op => {
      const retryCount = op.retryCount || 0;
      if (retryCount >= MAX_RETRIES) return false; // Max retries reached
      return op.syncStatus === 'pending' || op.syncStatus === 'failed';
    });

    if (pendingOps.length === 0) {
      isSyncing = false;
      return { successCount: 0, failCount: 0 };
    }

    // Sort by creation time
    pendingOps.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    const deviceId = localStorage.getItem('device_id') || 'dev-' + Math.random().toString(36).substring(2, 9);
    localStorage.setItem('device_id', deviceId);

    // Robust retry loop with exponential backoff per batch/request
    let attempt = 0;
    let response: Response | null = null;
    let lastError: any = null;

    while (attempt < 3) {
      try {
        response = await fetch('/api/sync', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': pendingOps[0]?.offlineId || 'batch-sync'
          },
          body: JSON.stringify({
            deviceId,
            clientVersion: '1.0.0',
            operations: pendingOps
          })
        });

        if (response.ok) break;
        throw new Error('Server sync endpoint returned status ' + response.status);
      } catch (err) {
        lastError = err;
        attempt++;
        if (attempt < 3) {
          const backoffDelay = Math.min(1000 * Math.pow(2, attempt), 10000);
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
        }
      }
    }

    if (!response || !response.ok) {
      throw lastError || new Error('Failed to sync queue after retries');
    }

    const data = await response.json();
    if (data.success && Array.isArray(data.results)) {
      for (const resItem of data.results) {
        if (resItem.status === 'completed') {
          await removePendingOperation(resItem.offlineId);
          successCount++;
        } else {
          const op = pendingOps.find(o => o.offlineId === resItem.offlineId);
          if (op) {
            op.retryCount = (op.retryCount || 0) + 1;
            op.syncStatus = op.retryCount >= MAX_RETRIES ? 'failed' : 'pending';
            op.lastError = resItem.message || 'Sync failed on server';
            await savePendingOperation(op);
          }
          failCount++;
        }
      }
      if (successCount > 0) {
        toast.success(`Successfully synchronised ${successCount} offline record(s).`);
      }
    }
  } catch (error: any) {
    console.error('Sync queue error with robust retry:', error);
    failCount++;
  } finally {
    isSyncing = false;
  }

  return { successCount, failCount };
}

export async function queueOfflineOperation(
  entityType: string,
  operationType: 'create' | 'update' | 'delete' | 'approve' | 'process',
  payload: Record<string, unknown>,
  userRole: string,
  createdBy: string,
  recordId?: string
): Promise<string> {
  const offlineId = 'offline-' + crypto.randomUUID();
  const deviceId = localStorage.getItem('device_id') || 'dev-' + Math.random().toString(36).substring(2, 9);
  localStorage.setItem('device_id', deviceId);

  const op: PendingOperation = {
    offlineId,
    entityType,
    operationType,
    recordId,
    payload,
    createdAt: new Date().toISOString(),
    createdBy,
    userRole,
    deviceId,
    syncStatus: 'pending',
    retryCount: 0,
    clientVersion: '1.0.0'
  };

  await savePendingOperation(op);
  toast.success('Saved offline successfully. This record will be synchronised automatically when the connection is restored.');
  return offlineId;
}
